import { cx } from '@emotion/css';
import { Menu, MenuItem } from '@mui/material';
import type { FunctionComponent, MouseEvent, ReactNode } from 'react';
import { useState } from 'react';
import EllipsisIcon from '../../assets/ellipsis.svg?react';
import IconButton from '../IconButton/IconButton';
import type { IconDropdownProps, MenuItemType } from './IconDropdown.model';
import { isSimpleMenuItem } from './IconDropdown.model';
import useStyles from './IconDropdown.styles';

/**
 * IconDropdown - A reusable dropdown menu component with icon button trigger
 *
 * Supports two ways of defining menu items:
 * 1. Simple configuration objects with label, onClick, etc.
 * 2. Full React components for custom markup
 *
 * @example
 * // Simple menu items
 * <IconDropdown
 *   items={[
 *     { key: 'edit', label: 'Edit', onClick: handleEdit },
 *     { key: 'delete', label: 'Delete', onClick: handleDelete },
 *     { key: 'archive', label: 'Archive', onClick: handleArchive },
 *   ]}
 * />
 *
 * @example
 * // Custom component menu items
 * <IconDropdown
 *   items={[
 *     <MenuItem key="custom" onClick={handleClick}>
 *       <CustomContent />
 *     </MenuItem>,
 *   ]}
 * />
 */
const IconDropdown: FunctionComponent<IconDropdownProps> = ({
  items,
  icon,
  iconButtonProps,
  menuProps,
  testId,
  disablePortal = false,
  onOpen,
  onClose,
}) => {
  const { classes } = useStyles();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const isOpen = Boolean(anchorEl);

  const handleClick = (event: MouseEvent<HTMLElement>): void => {
    setAnchorEl(event.currentTarget);
    onOpen?.();
  };

  const handleClose = (): void => {
    setAnchorEl(null);
    onClose?.();
  };

  const renderMenuItem = (item: MenuItemType, index: number): ReactNode => {
    // If it's a simple menu item configuration
    if (isSimpleMenuItem(item)) {
      if (item.hidden) {
        return null;
      }

      const handleItemClick = (): void => {
        item.onClick?.();
        handleClose();
      };

      // Use provided key or generate one from index
      const itemKey = item.key || `menu-item-${index}`;

      return (
        <MenuItem
          key={itemKey}
          onClick={handleItemClick}
          data-testid={item.testId}
          disabled={item.disabled}
          className={cx(classes.menuItem, item.className)}
          {...(item.href ? { component: 'a', href: item.href } : {})}
        >
          {item.label}
        </MenuItem>
      );
    }

    // Otherwise, render the component directly (it should already have a key)
    return item;
  };

  return (
    <>
      <IconButton
        icon={icon || <EllipsisIcon />}
        onClick={handleClick}
        data-testid={testId}
        className={classes.dropdownButton}
        {...iconButtonProps}
      />
      <Menu
        open={isOpen}
        onClose={handleClose}
        anchorEl={anchorEl}
        disablePortal={disablePortal}
        onClick={(e) => e.stopPropagation()}
        autoFocus={true}
        variant="menu"
        data-testid={`${testId}-menu`}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        classes={{
          paper: classes.menuPaper,
        }}
        MenuListProps={{
          'aria-labelledby': testId,
          autoFocusItem: true,
          ...menuProps?.MenuListProps,
        }}
        {...menuProps}
      >
        {items.map((item, index) => renderMenuItem(item, index))}
      </Menu>
    </>
  );
};

export default IconDropdown;
